import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useChapterPrompt } from '../hooks/useChapterPrompt';
import {
  PRIMARY,
  TEXT_DARK,
  BG_CARD,
  BORDER_LIGHT,
  TEXT_MUTED,
} from './legacy-ball/table-management/designTokens';

/**
 * ChapterSelectionPopup – Post-login modal prompting guests to select their chapter affiliation.
 *
 * Renders only when the useChapterPrompt hook indicates shouldPrompt === true and loading === false.
 * Self-contained component; no props required.
 */
export function ChapterSelectionPopup() {
  const { shouldPrompt, chapters, loading, error, dismiss, submit } =
    useChapterPrompt();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  // Focus trap and keyboard handling
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, input, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusableElements[0];

    // Focus the search input on mount
    first?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        dismissRef.current();
        return;
      }
      if (e.key === 'Tab') {
        // Re-query focusable elements in case the list changed (e.g., filtered chapters)
        const currentFocusable = modal!.querySelectorAll<HTMLElement>(
          'button, input, [tabindex]:not([tabindex="-1"])',
        );
        const currentFirst = currentFocusable[0];
        const currentLast = currentFocusable[currentFocusable.length - 1];

        if (e.shiftKey && document.activeElement === currentFirst) {
          e.preventDefault();
          currentLast?.focus();
        } else if (!e.shiftKey && document.activeElement === currentLast) {
          e.preventDefault();
          currentFirst?.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const filteredChapters = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return chapters;
    return chapters.filter((ch) => ch.name.toLowerCase().includes(term));
  }, [chapters, searchTerm]);

  if (loading || !shouldPrompt) {
    return null;
  }

  async function handleSubmit(
    action: 'select' | 'none' | 'suppress',
    chapterName?: string,
  ) {
    setSubmitting(true);
    try {
      await submit(action, chapterName);
    } catch {
      // error state is managed by the hook
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="chapter-popup-title">
      <div ref={modalRef} style={styles.card}>
        {/* Header */}
        <h2 id="chapter-popup-title" style={styles.title}>
          🏛️ Select Your Chapter
        </h2>

        {/* Search input */}
        <input
          type="text"
          placeholder="Search chapters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
          aria-label="Search chapters"
        />

        {/* Chapter list */}
        <div style={styles.listContainer}>
          {filteredChapters.length === 0 ? (
            <div style={styles.emptyMessage}>No chapters match your search.</div>
          ) : (
            filteredChapters.map((chapter) => (
              <button
                key={chapter.id}
                type="button"
                style={
                  selectedChapter === chapter.name
                    ? styles.chapterItemSelected
                    : styles.chapterItem
                }
                onClick={() => setSelectedChapter(chapter.name)}
                aria-pressed={selectedChapter === chapter.name}
              >
                {chapter.name}
              </button>
            ))
          )}
        </div>

        {/* Select button */}
        <button
          type="button"
          style={
            selectedChapter
              ? styles.selectButton
              : styles.selectButtonDisabled
          }
          disabled={!selectedChapter || submitting}
          onClick={() => handleSubmit('select', selectedChapter!)}
          aria-label="Select chapter"
        >
          {submitting ? 'Saving...' : 'Select Chapter'}
        </button>

        {/* Error message */}
        {error && (
          <div style={styles.errorContainer} role="alert">
            <span style={styles.errorText}>{error}</span>
            <button
              type="button"
              style={styles.retryButton}
              onClick={() => handleSubmit('select', selectedChapter!)}
              disabled={submitting}
            >
              Retry
            </button>
          </div>
        )}

        {/* Divider */}
        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <span style={styles.dividerLine} />
        </div>

        {/* Action buttons */}
        <div style={styles.actionRow}>
          <button
            type="button"
            style={styles.actionButton}
            onClick={() => handleSubmit('none')}
            disabled={submitting}
          >
            None
          </button>
          <button
            type="button"
            style={styles.actionButton}
            onClick={() => handleSubmit('suppress')}
            disabled={submitting}
          >
            Don't Ask Again
          </button>
          <button
            type="button"
            style={styles.actionButton}
            onClick={() => dismiss()}
            disabled={submitting}
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Inline styles ---------- */

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  card: {
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '32px 28px 24px',
    maxWidth: '440px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.95rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
    color: TEXT_DARK,
  },
  listContainer: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
  },
  chapterItem: {
    display: 'block',
    width: '100%',
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '0.9rem',
    background: 'none',
    border: 'none',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    cursor: 'pointer',
    color: TEXT_DARK,
  },
  chapterItemSelected: {
    display: 'block',
    width: '100%',
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '0.9rem',
    background: `${PRIMARY}15`,
    border: 'none',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    cursor: 'pointer',
    color: PRIMARY,
    fontWeight: 600,
  },
  selectButton: {
    padding: '12px 16px',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: PRIMARY,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  selectButtonDisabled: {
    padding: '12px 16px',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#a0b8a8',
    border: 'none',
    borderRadius: '6px',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
  },
  errorText: {
    flex: 1,
    fontSize: '0.85rem',
    color: '#991b1b',
  },
  retryButton: {
    padding: '4px 10px',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#991b1b',
    backgroundColor: '#fecaca',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: BORDER_LIGHT,
  },
  dividerText: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    textTransform: 'lowercase',
  },
  actionRow: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  actionButton: {
    padding: '8px 14px',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: TEXT_DARK,
    backgroundColor: '#f3f4f6',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    cursor: 'pointer',
  },
  emptyMessage: {
    padding: '16px',
    textAlign: 'center',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
};

export default ChapterSelectionPopup;
